import React, { useEffect, useMemo, useState, useRef } from "react";

import { Link, useSearchParams } from "react-router-dom";

import Swal from "sweetalert2";

import CertificationServices from "../../../services/CertificationServices";

import AppLayout from '../../common/Navigation/AppLayout';

import PageHeader from '../../common/Navigation/PageHeader';

import { groupCertifications, getSortedGroupNames, groupCourses, getAllGroupNames } from '../../../config/certificationGroups';

const statusLabels = {

  pending_review: "Pending Review",

  approved: "Approved",

  completed: "Completed",

  in_progress: "In Progress",

  not_started: "Not Started",

  expired: "Expired",

  rejected: "Rejected",

};

const extractTeacherRecords = (payload) => {

  const list = (() => {

    if (!payload) return [];

    if (Array.isArray(payload)) return payload;

    if (Array.isArray(payload?.certifications)) return payload.certifications;

    if (Array.isArray(payload?.teachers)) return payload.teachers;

    if (Array.isArray(payload?.records)) return payload.records;

    if (Array.isArray(payload?.data)) return payload.data;

    return [];

  })();

  return list.map((entry) => {

    const teacher =

      entry.teacher ||

      entry.teacherInfo ||

      entry.user ||

      entry.profile ||

      {};

    const certifications = Array.isArray(entry.certifications)

      ? entry.certifications

      : Array.isArray(entry.records)

      ? entry.records

      : Array.isArray(entry.courses)

      ? entry.courses

      : Array.isArray(entry.items)

      ? entry.items

      : [];

    const teacherId =

      teacher.id ||

      teacher._id ||

      entry.teacherId ||

      entry.userId ||

      entry.id ||

      "";

    const teacherName =

      teacher.name ||

      teacher.fullName ||

      `${teacher.firstName || ""} ${teacher.lastName || ""}`.trim() ||

      entry.teacherName ||

      "Unknown Teacher";

    return {

      teacherId,

      teacherName: teacherName || "Unknown Teacher",

      teacherEmail: teacher.email || entry.teacherEmail || "",

      certifications,

    };

  });

};

const getSectionKey = (cert = {}) => {

  const section = cert.metadata?.section || cert.section || "unassigned";

  

  // Replace section_v with core_active_shooter

  if (section.toLowerCase() === "section_v") {

    return "core_active_shooter";

  }

  

  return section;

};

const getStatusBadgeClass = (status = "") => {

  const normalized = status.toLowerCase();

  switch (normalized) {

    case "completed":

    case "approved":

      return "badge bg-success-subtle text-success";

    case "pending_review":

      return "badge bg-warning-subtle text-warning";

    case "rejected":

    case "expired":

      return "badge bg-danger-subtle text-danger";

    case "in_progress":

      return "badge bg-info-subtle text-info";

    default:

      return "badge bg-light text-dark";

  }

};

const getCertificateIdentifier = (cert = {}, teacherId = "") => {

  return (

    cert._id ||

    cert.id ||

    cert.certificationId ||

    `${teacherId || "teacher"}-${cert.courseKey || "record"}`

  );

};

const formatDate = (value) => {

  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString();

};

const statusOptions = [

  "pending_review",

  "in_progress",

  "completed",

  "approved",

  "not_started",

  "expired",

  "rejected",

];

const normalizeCourses = (payload) => {

  const list = (() => {

    if (!payload) return [];

    if (Array.isArray(payload)) return payload;

    if (Array.isArray(payload?.courses)) return payload.courses;

    if (Array.isArray(payload?.data)) return payload.data;

    return [];

  })();

  return list

    .map((course) => {

      const key =

        course.courseKey || course.key || course.id || course._id || "";

      const label =

        course.title ||

        course.name ||

        course.courseTitle ||

        course.displayName ||

        key;

      // Preserve displayGroup and displayPriority from backend for proper grouping

      return key ? { 

        key, 

        label,

        displayGroup: course.displayGroup,

        displayPriority: course.displayPriority,

        // Preserve other course fields that might be needed

        ...course

      } : null;

    })

    .filter(Boolean);

};

const toDateInputValue = (value) => {

  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "";

  return date.toISOString().slice(0, 10);

};

const SchoolCertifications = () => {

  const [searchParams, setSearchParams] = useSearchParams();

  const [records, setRecords] = useState([]);

  const [isLoading, setIsLoading] = useState(true);

  const [statusFilter, setStatusFilter] = useState("all");

  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");

  const [refreshing, setRefreshing] = useState(false);

  const [totals, setTotals] = useState({});

  const [downloadingId, setDownloadingId] = useState(null);

  const [sortBy, setSortBy] = useState("name"); // "name" or "category"

  const [sortOrder, setSortOrder] = useState("asc"); // "asc" or "desc"

  

  // Add certification form state

  const [showAddForm, setShowAddForm] = useState(false);

  const [courses, setCourses] = useState([]);

  const [isLoadingCourses, setIsLoadingCourses] = useState(false);

  const [isSavingCert, setIsSavingCert] = useState(false);

  const [isUploadingCert, setIsUploadingCert] = useState(false);

  const [selectedCertification, setSelectedCertification] = useState(null);

  const [selectedGroup, setSelectedGroup] = useState("");

  const [addToAllTeachers, setAddToAllTeachers] = useState(false);

  const fileInputRef = useRef(null);

  const [formState, setFormState] = useState({

    teacherId: "",

    courseKey: "",

    status: "pending_review",

    dateCompleted: "",

    renewByDate: "",

    hours: "",

    notes: "",

  });

  const fetchData = async () => {

    setIsLoading(true);

    try {

      const response = await CertificationServices.getSchoolCertifications();

      const normalized = extractTeacherRecords(response);

      setRecords(normalized);

      setTotals(

        response && typeof response === "object" && response.totals

          ? response.totals

          : {}

      );

    } catch (error) {

      console.error("Failed to load school certifications", error);

      Swal.fire(

        "Error",

        "We couldn't load certification records for your staff. Please try again.",

        "error"

      );

    } finally {

      setIsLoading(false);

    }

  };

  const loadCourses = async () => {

    setIsLoadingCourses(true);

    try {

      const response = await CertificationServices.getCourses();

      const normalized = normalizeCourses(response);

      setCourses(normalized);

      

      // Initialize group and course selection

      if (normalized.length > 0) {

        const grouped = groupCourses(normalized);

        const groupNames = getAllGroupNames();

        const firstGroupWithCourses = groupNames.find(groupName => 

          grouped[groupName] && grouped[groupName].courses.length > 0

        );

        

        if (firstGroupWithCourses && !selectedGroup) {

          setSelectedGroup(firstGroupWithCourses);

          const firstCourseInGroup = grouped[firstGroupWithCourses].courses[0];

          const firstKey = firstCourseInGroup.key || firstCourseInGroup.courseKey || firstCourseInGroup.id || firstCourseInGroup._id;

          if (!formState.courseKey) {

            setFormState(prev => ({ ...prev, courseKey: firstKey }));

          }

        } else if (!formState.courseKey && normalized.length > 0) {

          const firstKey = normalized[0].key || normalized[0].courseKey || normalized[0].id || normalized[0]._id;

          setFormState(prev => ({ ...prev, courseKey: firstKey }));

        }

      }

    } catch (error) {

      console.error("Failed to load courses", error);

      Swal.fire("Error", "Could not load certification courses.", "error");

    } finally {

      setIsLoadingCourses(false);

    }

  };

  useEffect(() => {

    fetchData();

    loadCourses();

    // Check if there's a search parameter in the URL

    const urlSearch = searchParams.get("search");

    if (urlSearch) {

      setSearchTerm(urlSearch);

    }

    // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [searchParams]);

  // Update URL when search term changes (but don't trigger on initial load)

  useEffect(() => {

    if (searchTerm) {

      setSearchParams({ search: searchTerm }, { replace: true });

    } else {

      setSearchParams({}, { replace: true });

    }

  }, [searchTerm, setSearchParams]);

  const filteredRecords = useMemo(() => {

    const term = searchTerm.trim().toLowerCase();

    let filtered = records

      .map((teacher) => {

        const filteredCerts = teacher.certifications.filter((cert) => {

          const status = (cert.status || "").toLowerCase();

          const matchStatus =

            statusFilter === "all" || status === statusFilter.toLowerCase();

          const courseLabel = (

            cert.courseTitle ||

            cert.courseName ||

            cert.course?.title ||

            cert.courseKey ||

            ""

          )

            .toString()

            .toLowerCase();

          const matchTerm =

            !term ||

            teacher.teacherName.toLowerCase().includes(term) ||

            teacher.teacherEmail.toLowerCase().includes(term) ||

            courseLabel.includes(term);

          return matchStatus && matchTerm;

        });

        return { ...teacher, certifications: filteredCerts };

      })

      .filter((teacher) => teacher.certifications.length > 0);

    // Apply sorting

    filtered = [...filtered].sort((a, b) => {

      if (sortBy === "name") {

        const nameA = a.teacherName.toLowerCase();

        const nameB = b.teacherName.toLowerCase();

        const comparison = nameA.localeCompare(nameB);

        return sortOrder === "asc" ? comparison : -comparison;

      } else if (sortBy === "category") {

        // Get the first certification's category/group for each teacher using groupCertifications

        const getFirstCategory = (teacher) => {

          if (teacher.certifications.length === 0) return "Other Certifications";

          const grouped = groupCertifications(teacher.certifications);

          const sortedGroupNames = getSortedGroupNames(grouped);

          return sortedGroupNames.length > 0 ? sortedGroupNames[0] : "Other Certifications";

        };

        

        const categoryA = getFirstCategory(a);

        const categoryB = getFirstCategory(b);

        const comparison = categoryA.localeCompare(categoryB);

        return sortOrder === "asc" ? comparison : -comparison;

      }

      return 0;

    });

    return filtered;

  }, [records, statusFilter, searchTerm, sortBy, sortOrder]);

  const totalPending = useMemo(() => {

    return records.reduce((count, teacher) => {

      return (

        count +

        teacher.certifications.filter(

          (cert) => cert.status === "pending_review"

        ).length

      );

    }, 0);

  }, [records]);

  const sectionCounts = useMemo(() => {

    const counts = {};

    

    // Count all certifications by section, replacing section_v with core_active_shooter

    records.forEach((teacher) => {

      teacher.certifications.forEach((cert) => {

        const key = getSectionKey(cert);

        counts[key] = (counts[key] || 0) + 1;

      });

    });

    

    // Merge with backend totals if available, but convert section_v to core_active_shooter

    if (

      totals &&

      typeof totals === "object" &&

      Object.keys(totals).length > 0

    ) {

      Object.entries(totals).forEach(([key, count]) => {

        const normalizedKey = key.toLowerCase() === "section_v" ? "core_active_shooter" : key;

        // Use backend count if we don't have a calculated count, or merge them

        if (!counts[normalizedKey] || counts[normalizedKey] < count) {

          counts[normalizedKey] = count;

        }

      });

    }

    

    // Remove section_v from counts if it exists (it's been replaced by core_active_shooter)

    if (counts.section_v) {

      delete counts.section_v;

    }

    

    return counts;

  }, [records, totals]);

  const overallCount = useMemo(() => {

    return records.reduce(

      (count, teacher) => count + teacher.certifications.length,

      0

    );

  }, [records]);

  const handleRefresh = async () => {

    setRefreshing(true);

    await fetchData();

    setRefreshing(false);

  };

  // Group courses for category dropdown

  const groupedCourses = useMemo(() => groupCourses(courses), [courses]);

  

  // Get available groups (only those with courses)

  const availableGroups = useMemo(() => {

    const allGroups = getAllGroupNames();

    return allGroups.filter(groupName => 

      groupedCourses[groupName] && groupedCourses[groupName].courses.length > 0

    );

  }, [groupedCourses]);

  // Get courses filtered by selected group

  const filteredCourses = useMemo(() => {

    if (!selectedGroup || !groupedCourses[selectedGroup]) {

      return courses; // Fallback to all courses if no group selected

    }

    return groupedCourses[selectedGroup].courses;

  }, [selectedGroup, groupedCourses, courses]);

  const resetForm = () => {

    setFormState({

      teacherId: "",

      courseKey: "",

      status: "pending_review",

      dateCompleted: "",

      renewByDate: "",

      hours: "",

      notes: "",

    });

    setSelectedCertification(null);

    setAddToAllTeachers(false);

    if (fileInputRef.current) {

      fileInputRef.current.value = "";

    }

    // Reset to first group and course

    if (availableGroups.length > 0 && !selectedGroup) {

      setSelectedGroup(availableGroups[0]);

      if (groupedCourses[availableGroups[0]] && groupedCourses[availableGroups[0]].courses.length > 0) {

        const firstCourse = groupedCourses[availableGroups[0]].courses[0];

        const firstKey = firstCourse.key || firstCourse.courseKey || firstCourse.id || firstCourse._id;

        setFormState(prev => ({ ...prev, courseKey: firstKey }));

      }

    }

  };

  const handleFormChange = (event) => {

    const { name, value } = event.target;

    setFormState((prev) => ({

      ...prev,

      [name]: value,

    }));

  };

  const handleGroupChange = (event) => {

    const newGroup = event.target.value;

    setSelectedGroup(newGroup);

    // Auto-select first course in the new group

    if (groupedCourses[newGroup] && groupedCourses[newGroup].courses.length > 0) {

      const firstCourse = groupedCourses[newGroup].courses[0];

      const firstKey = firstCourse.key || firstCourse.courseKey || firstCourse.id || firstCourse._id;

      setFormState(prev => ({ ...prev, courseKey: firstKey }));

    }

  };

  const getCourseKey = (course) => {

    return course.key || course.courseKey || course.id || course._id || "";

  };

  const getCourseLabel = (course) => {

    return course.label || course.title || course.courseTitle || course.name || course.key || "";

  };

  const validateFile = (file) => {

    if (!file) return false;

    const allowedTypes = [

      "application/pdf",

      "image/jpeg",

      "image/png",

      "image/jpg",

    ];

    if (!allowedTypes.includes(file.type)) {

      Swal.fire(

        "Invalid file",

        "Certificates must be a PDF, JPG, or PNG file.",

        "warning"

      );

      return false;

    }

    const maxBytes = 5 * 1024 * 1024;

    if (file.size > maxBytes) {

      Swal.fire(

        "File too large",

        "Certificates must be 5 MB or smaller.",

        "warning"

      );

      return false;

    }

    return true;

  };

  const handleSaveCertification = async (event) => {

    event.preventDefault();

    

    if (!addToAllTeachers && !formState.teacherId) {

      Swal.fire("Teacher Required", "Please select a teacher or check 'Add to all teachers'.", "warning");

      return;

    }

    if (!formState.courseKey) {

      Swal.fire("Course Required", "Please choose a course.", "warning");

      return;

    }

    setIsSavingCert(true);

    try {

      const payload = {

        courseKey: formState.courseKey,

        status: formState.status || undefined,

        dateCompleted: formState.dateCompleted || undefined,

        renewByDate: formState.renewByDate || undefined,

        hours: formState.hours ? Number(formState.hours) : undefined,

        notes: formState.notes || undefined,

      };

      if (selectedCertification) {

        payload.certificationId =

          selectedCertification._id ||

          selectedCertification.id ||

          selectedCertification.certificationId;

      }

      // Determine which teachers to assign to

      const teachersToAssign = addToAllTeachers 

        ? records.map(t => t.teacherId)

        : [formState.teacherId];

      if (teachersToAssign.length === 0) {

        Swal.fire("Error", "No teachers selected.", "error");

        setIsSavingCert(false);

        return;

      }

      // Save certification for each teacher

      const results = [];

      for (const teacherId of teachersToAssign) {

        try {

          const result = await CertificationServices.assignCertificationToTeacher(

            teacherId,

            payload

          );

          results.push({ teacherId, result, success: true });

        } catch (err) {

          console.error(`Failed to assign to teacher ${teacherId}:`, err);

          results.push({ teacherId, error: err, success: false });

        }

      }

      // Handle file upload if provided

      const file = fileInputRef.current?.files?.[0];

      if (file && validateFile(file)) {

        setIsUploadingCert(true);

        let uploadSuccessCount = 0;

        let uploadFailCount = 0;

        for (const { teacherId, result, success } of results) {

          if (!success) continue;

          

          const certificationData = result?.certification || result || {};

          const certId =

            certificationData._id ||

            certificationData.id ||

            certificationData.certificationId;

          if (certId) {

            try {

              await CertificationServices.uploadCertificate(certId, file);

              uploadSuccessCount++;

            } catch (uploadError) {

              console.error(`Failed to upload certificate for teacher ${teacherId}:`, uploadError);

              uploadFailCount++;

            }

          } else {

            uploadFailCount++;

          }

        }

        setIsUploadingCert(false);

        if (uploadSuccessCount > 0 && uploadFailCount === 0) {

          Swal.fire("Success", `Certification and certificate file saved for ${uploadSuccessCount} teacher(s).`, "success");

        } else if (uploadSuccessCount > 0) {

          Swal.fire("Partial Success", `Certification saved for ${results.length} teacher(s). Certificate uploaded for ${uploadSuccessCount} teacher(s).`, "warning");

        } else {

          Swal.fire("Warning", "Certification saved but certificate upload failed. You can upload files later by editing individual certifications.", "warning");

        }

      } else {

        const successCount = results.filter(r => r.success).length;

        const failCount = results.filter(r => !r.success).length;

        

        if (failCount === 0) {

          Swal.fire("Saved", `Certification saved for ${successCount} teacher(s).`, "success");

        } else {

          Swal.fire("Partial Success", `Certification saved for ${successCount} teacher(s). Failed for ${failCount} teacher(s).`, "warning");

        }

      }

      await fetchData();

      resetForm();

      setShowAddForm(false);

    } catch (error) {

      console.error("Failed to save certification", error);

      Swal.fire(

        "Error",

        "We could not save the certification. Please try again.",

        "error"

      );

    } finally {

      setIsSavingCert(false);

    }

  };

  const handleEditCertification = (teacherId, cert) => {

    // Find the group for this course

    const courseKey = cert.courseKey || cert.key || "";

    const grouped = groupCourses(courses);

    const groupNames = getAllGroupNames();

    const courseGroup = groupNames.find(groupName => {

      const group = grouped[groupName];

      return group && group.courses.some(c => getCourseKey(c) === courseKey);

    });

    

    if (courseGroup) {

      setSelectedGroup(courseGroup);

    }

    

    setFormState({

      teacherId: teacherId,

      courseKey: courseKey,

      status: cert.status || "pending_review",

      dateCompleted: toDateInputValue(cert.dateCompleted),

      renewByDate: toDateInputValue(cert.renewByDate),

      hours: cert.hours !== undefined && cert.hours !== null ? String(cert.hours) : "",

      notes: cert.notes || "",

    });

    setSelectedCertification(cert);

    setAddToAllTeachers(false); // Can't add to all when editing

    setShowAddForm(true);

  };

  const handleDownloadCertificate = async (teacherId, cert) => {

    if (!cert?.certificateUrl) {

      Swal.fire(

        "No certificate",

        "There is no certificate file available for this record yet.",

        "info"

      );

      return;

    }

    const certIdentifier = getCertificateIdentifier(cert, teacherId);

    setDownloadingId(certIdentifier);

    try {

      const courseLabel =

        cert.courseTitle ||

        cert.courseName ||

        cert.course?.title ||

        "Certificate";

      const safeCourseLabel = courseLabel.replace(/[\\/:*?"<>|]+/g, "_");

      const { blob, filename, contentType } =

        await CertificationServices.downloadCertificate({

          teacherId,

          certId:

            cert._id || cert.id || cert.certificationId || certIdentifier,

          fallbackName: `${safeCourseLabel}.pdf`,

        });

      const downloadName = filename || `${courseLabel}.pdf`;

      const blobUrl = window.URL.createObjectURL(

        new Blob([blob], { type: contentType })

      );

      const link = document.createElement("a");

      link.href = blobUrl;

      link.download = downloadName;

      document.body.appendChild(link);

      link.click();

      link.remove();

      window.URL.revokeObjectURL(blobUrl);

    } catch (error) {

      console.error("Failed to download certificate", error);

      Swal.fire(

        "Download failed",

        "We couldn't download this certificate. Please try again.",

        "error"

      );

    } finally {

      setDownloadingId(null);

    }

  };

  const userobj = JSON.parse(localStorage.getItem('user') || '{}');
  
  return (

    <AppLayout breadcrumbTitle="Staff Certifications" role={1} userType={userobj?.type} activeMenu="Certifications" layoutType="topnav">

      <PageHeader title="Certification Overview" subtitle="Track course completion, renewals, and certificate files for each staff member." />

      <div className="card">

        <div className="card-body">

          <div className="d-flex flex-column flex-xl-row gap-3 align-items-xl-center justify-content-between mb-4">

            <div className="d-flex flex-wrap gap-2">

                      <Link

                        to="/certification-catalog"

                        className="btn btn-outline-info"

                      >

                        View Certification Catalog

                      </Link>

                      <button

                        type="button"

                        className="btn btn-primary"

                        onClick={() => {

                          setShowAddForm(!showAddForm);

                          if (!showAddForm) {

                            resetForm();

                          }

                        }}

                      >

                        {showAddForm ? "Cancel" : "Add Certification"}

                      </button>

                      <button

                        type="button"

                        className="btn btn-outline-secondary"

                        onClick={handleRefresh}

                        disabled={refreshing || isLoading}

                      >

                        {refreshing ? "Refreshing..." : "Refresh"}

                      </button>

                      <span className="badge bg-warning-subtle text-warning fw-semibold align-self-center">

                        Pending review: {totalPending}

                      </span>

                    </div>

                  </div>

                  

                  {/* Add Certification Form */}

                  {showAddForm && (

                    <div className="card mb-4 border-primary">

                      <div className="card-body">

                        <div className="d-flex align-items-center justify-content-between flex-wrap mb-3">

                          <div>

                            <h5 className="mb-1">

                              Certification Tracker

                            </h5>

                            <p className="text-muted mb-0">

                              Track completion, renewal, and upload certificates for your required SOP courses.

                            </p>

                          </div>

                        </div>

                        <form onSubmit={handleSaveCertification}>

                          <div className="row g-3">

                            <div className="col-md-6">

                              <label className="form-label">Select category</label>

                              <select

                                className="form-control"

                                value={selectedGroup}

                                onChange={handleGroupChange}

                                disabled={isLoadingCourses}

                              >

                                {availableGroups.map((groupName) => (

                                  <option key={groupName} value={groupName}>

                                    {groupName}

                                  </option>

                                ))}

                              </select>

                            </div>

                            <div className="col-md-6">

                              <label className="form-label">Select course *</label>

                              <select

                                className="form-control"

                                name="courseKey"

                                value={formState.courseKey}

                                onChange={handleFormChange}

                                required

                                disabled={isLoadingCourses || !selectedGroup || filteredCourses.length === 0}

                              >

                                <option value="">Select a course</option>

                                {filteredCourses.map((course) => {

                                  const key = getCourseKey(course);

                                  return (

                                    <option key={key} value={key}>

                                      {getCourseLabel(course)}

                                    </option>

                                  );

                                })}

                              </select>

                            </div>

                            <div className="col-md-6">

                              <label className="form-label">Teacher *</label>

                              <select

                                className="form-control"

                                name="teacherId"

                                value={formState.teacherId}

                                onChange={handleFormChange}

                                required={!addToAllTeachers}

                                disabled={!!selectedCertification || addToAllTeachers}

                              >

                                <option value="">Select a teacher</option>

                                {records.map((teacher) => (

                                  <option key={teacher.teacherId} value={teacher.teacherId}>

                                    {teacher.teacherName} {teacher.teacherEmail ? `(${teacher.teacherEmail})` : ''}

                                  </option>

                                ))}

                              </select>

                            </div>

                            <div className="col-md-6 d-flex align-items-end">

                              <div className="form-check">

                                <input

                                  className="form-check-input"

                                  type="checkbox"

                                  id="addToAllTeachers"

                                  checked={addToAllTeachers}

                                  onChange={(e) => {

                                    setAddToAllTeachers(e.target.checked);

                                    if (e.target.checked) {

                                      setFormState(prev => ({ ...prev, teacherId: "" }));

                                    }

                                  }}

                                  disabled={!!selectedCertification}

                                />

                                <label className="form-check-label" htmlFor="addToAllTeachers">

                                  Add to all teachers ({records.length} teachers)

                                </label>

                              </div>

                            </div>

                            <div className="col-md-6">

                              <label className="form-label">Date completed</label>

                              <input

                                type="date"

                                className="form-control"

                                name="dateCompleted"

                                value={formState.dateCompleted}

                                onChange={handleFormChange}

                              />

                            </div>

                            <div className="col-md-6">

                              <label className="form-label">Renew by date</label>

                              <input

                                type="date"

                                className="form-control"

                                name="renewByDate"

                                value={formState.renewByDate}

                                onChange={handleFormChange}

                              />

                            </div>

                            <div className="col-md-6">

                              <label className="form-label">Hours spent</label>

                              <input

                                type="number"

                                min="0"

                                step="0.1"

                                className="form-control"

                                name="hours"

                                value={formState.hours}

                                onChange={handleFormChange}

                                placeholder="e.g. 1.5"

                              />

                            </div>

                            <div className="col-md-6">

                              <label className="form-label">Status</label>

                              <select

                                className="form-control"

                                name="status"

                                value={formState.status}

                                onChange={handleFormChange}

                              >

                                {statusOptions.map((value) => (

                                  <option key={value} value={value}>

                                    {statusLabels[value] ||

                                      value.charAt(0).toUpperCase() + value.slice(1)}

                                  </option>

                                ))}

                              </select>

                            </div>

                            <div className="col-12">

                              <label className="form-label">Notes for reviewers</label>

                              <textarea

                                className="form-control"

                                rows="3"

                                name="notes"

                                value={formState.notes}

                                onChange={handleFormChange}

                                placeholder="Add context for school administrators (optional)"

                              />

                            </div>

                            <div className="col-12">

                              <label className="form-label">Certificate upload</label>

                              {fileInputRef.current?.files?.[0] ? (

                                <div className="mb-2">

                                  <span className="text-success">

                                    <i className="feather-check me-1"></i>

                                    {fileInputRef.current.files[0].name} ({(fileInputRef.current.files[0].size / 1024).toFixed(2)} KB)

                                  </span>

                                  <button

                                    type="button"

                                    className="btn btn-sm btn-link text-danger"

                                    onClick={() => {

                                      if (fileInputRef.current) {

                                        fileInputRef.current.value = "";

                                      }

                                    }}

                                  >

                                    Remove

                                  </button>

                                </div>

                              ) : (

                                <p className="text-muted small mb-2">

                                  No certificate uploaded yet. Upload a PDF, JPG, or PNG file (max 5 MB) once you have completed the course.

                                </p>

                              )}

                              <input

                                ref={fileInputRef}

                                type="file"

                                className="form-control"

                                accept=".pdf,.jpg,.jpeg,.png"

                                disabled={isUploadingCert}

                              />

                              <small className="form-text text-muted">

                                Maximum file size: 5 MB

                              </small>

                            </div>

                          </div>

                          <div className="d-flex gap-2 mt-4">

                            <button

                              type="submit"

                              className="btn btn-primary"

                              disabled={isSavingCert || isUploadingCert}

                            >

                              {isSavingCert || isUploadingCert

                                ? "Saving..."

                                : selectedCertification

                                ? "Update Certification"

                                : "Add Certification"}

                            </button>

                            <button

                              type="button"

                              className="btn btn-outline-secondary"

                              onClick={() => {

                                resetForm();

                                setShowAddForm(false);

                              }}

                              disabled={isSavingCert || isUploadingCert}

                            >

                              Cancel

                            </button>

                          </div>

                        </form>

                      </div>

                    </div>

                  )}

                  <div className="row g-3 mb-3">

                    <div className="col-md-3">

                      <label className="form-label">Filter by status</label>

                      <select

                        className="form-control"

                        value={statusFilter}

                        onChange={(event) =>

                          setStatusFilter(event.target.value)

                        }

                      >

                        <option value="all">All statuses</option>

                        <option value="pending_review">Pending Review</option>

                        <option value="approved">Approved</option>

                        <option value="completed">Completed</option>

                        <option value="in_progress">In Progress</option>

                        <option value="expired">Expired</option>

                      </select>

                    </div>

                    <div className="col-md-3">

                      <label className="form-label">Sort by</label>

                      <select

                        className="form-control"

                        value={sortBy}

                        onChange={(event) => setSortBy(event.target.value)}

                      >

                        <option value="name">Name</option>

                        <option value="category">Certification Category</option>

                      </select>

                    </div>

                    <div className="col-md-2">

                      <label className="form-label">Order</label>

                      <select

                        className="form-control"

                        value={sortOrder}

                        onChange={(event) => setSortOrder(event.target.value)}

                      >

                        <option value="asc">Ascending</option>

                        <option value="desc">Descending</option>

                      </select>

                    </div>

                    <div className="col-md-4">

                      <label className="form-label">Search</label>

                      <input

                        type="text"

                        className="form-control"

                        placeholder="Teacher or course name"

                        value={searchTerm}

                        onChange={(event) => setSearchTerm(event.target.value)}

                      />

                    </div>

                  </div>

                  {isLoading ? (

                    <div className="text-center py-5">

                      <div className="spinner-border text-primary" role="status">

                        <span className="visually-hidden">Loading...</span>

                      </div>

                    </div>

                  ) : filteredRecords.length === 0 ? (

                    <div className="alert alert-info mb-0">

                      No certification records match your filters yet. Encourage

                      your staff to update their certification progress.

                    </div>

                  ) : (

                    filteredRecords.map((teacher) => (

                      <div className="card mb-4" key={teacher.teacherId}>

                        <div className="card-body">

                          <div className="d-flex flex-column flex-md-row justify-content-between gap-2">

                            <div>

                              <h5 className="mb-1">{teacher.teacherName}</h5>

                              <p className="text-muted mb-0">

                                {teacher.teacherEmail || "No email on file"}

                              </p>

                            </div>

                            <div className="text-md-end">

                              <span className="badge bg-light text-dark">

                                Total tracked: {teacher.certifications.length}

                              </span>

                            </div>

                          </div>

                  <div className="table-responsive mt-3">

                    <table className="table table-striped align-middle">

                              <thead>

                                <tr>

                                  <th>Course</th>

                                  <th>Status</th>

                                  <th>Completed</th>

                                  <th>Renew by</th>

                                  <th>Hours</th>

                                  <th>Certificate</th>

                                  <th>Actions</th>

                                </tr>

                              </thead>

                              <tbody>

                                {(() => {

                                  const grouped = groupCertifications(teacher.certifications);

                                  const sortedGroupNames = getSortedGroupNames(grouped);

                                  return sortedGroupNames.map((groupName) => {

                                    const group = grouped[groupName];

                                    if (!group || group.certifications.length === 0) return null;

                                    return (

                                      <React.Fragment key={groupName}>

                                        {/* Group Header Row */}

                                        <tr className="table-group-header" style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>

                                          <td colSpan="7" style={{ padding: '12px 16px', fontSize: '1.1rem', borderTop: '2px solid #dee2e6' }}>

                                            {groupName}

                                          </td>

                                        </tr>

                                        {/* Group Certifications */}

                                        {group.certifications.map((cert) => {

                                          const certIdentifier = getCertificateIdentifier(

                                            cert,

                                            teacher.teacherId

                                          );

                                          const statusKey = cert.status || "not_started";

                                          const badgeLabel =

                                            statusLabels[statusKey] ||

                                            statusKey

                                              .split(/[_\s-]+/)

                                              .map(

                                                (part) =>

                                                  part.charAt(0).toUpperCase() +

                                                  part.slice(1)

                                              )

                                              .join(" ");

                                          const badgeClass = getStatusBadgeClass(

                                            statusKey

                                          );

                                          const courseTitle =

                                            cert.courseTitle ||

                                            cert.courseName ||

                                            cert.course?.title ||

                                            cert.courseKey ||

                                            "Certification Course";

                                          

                                          // Handle multiple possible field names for dates and hours

                                          const dateCompleted = cert.dateCompleted || cert.date_completed || cert.completedDate || cert.completed_date;

                                          const renewByDate = cert.renewByDate || cert.renew_by_date || cert.renewBy || cert.renew_by || cert.renewalDate || cert.renewal_date;

                                          const hours = cert.hours !== undefined && cert.hours !== null 

                                            ? cert.hours 

                                            : (cert.hoursSpent !== undefined && cert.hoursSpent !== null ? cert.hoursSpent : null) ||

                                              (cert.hours_spent !== undefined && cert.hours_spent !== null ? cert.hours_spent : null);

                                          

                                          return (

                                            <tr

                                              key={`${teacher.teacherId}-${cert.courseKey || cert._id || cert.id}`}

                                            >

                                              <td>{courseTitle}</td>

                                              <td>

                                                <span className={badgeClass}>

                                                  {badgeLabel}

                                                </span>

                                              </td>

                                              <td>

                                                {dateCompleted

                                                  ? formatDate(dateCompleted)

                                                  : "—"}

                                              </td>

                                              <td>

                                                {renewByDate

                                                  ? formatDate(renewByDate)

                                                  : "—"}

                                              </td>

                                              <td>

                                                {hours !== undefined && hours !== null

                                                  ? hours

                                                  : "—"}

                                              </td>

                                              <td>

                                                {cert.certificateUrl ? (

                                                  <button

                                                    type="button"

                                                    className="btn btn-sm btn-outline-primary"

                                                    onClick={() =>

                                                      handleDownloadCertificate(

                                                        teacher.teacherId,

                                                        cert

                                                      )

                                                    }

                                                    disabled={

                                                      downloadingId === certIdentifier

                                                    }

                                                  >

                                                    {downloadingId === certIdentifier

                                                      ? "Downloading..."

                                                      : "Download"}

                                                  </button>

                                                ) : (

                                                  <span className="text-muted">

                                                    No file

                                                  </span>

                                                )}

                                              </td>

                                              <td>

                                                <button

                                                  type="button"

                                                  className="btn btn-sm btn-outline-secondary"

                                                  onClick={() =>

                                                    handleEditCertification(

                                                      teacher.teacherId,

                                                      cert

                                                    )

                                                  }

                                                >

                                                  Edit

                                                </button>

                                              </td>

                                            </tr>

                                          );

                                        })}

                                      </React.Fragment>

                                    );

                                  });

                                })()}

                              </tbody>

                            </table>

                          </div>

                          <div className="d-flex flex-wrap gap-2 mt-3">

                            <a

                              href={

                                teacher.teacherEmail

                                  ? `mailto:${teacher.teacherEmail}`

                                  : "#"

                              }

                              onClick={(event) => {

                                if (!teacher.teacherEmail) {

                                  event.preventDefault();

                                  Swal.fire(

                                    "No email available",

                                    "This teacher does not have an email address on file.",

                                    "info"

                                  );

                                }

                              }}

                              className="btn btn-outline-secondary btn-sm"

                            >

                              Email reminder

                            </a>

                          </div>

                        </div>

                      </div>

                    ))

                  )}

                </div>

              </div>

    </AppLayout>

  );

};

export default SchoolCertifications;
